import type { Placement } from "@floating-ui/dom";

import { useAnchorPosition, usePopover } from "../interaction";
import { cn } from "../lib/cn";

/** Side and alignment vocabulary from @floating-ui/dom, re-exported so callers need one import. */
export type { Placement } from "@floating-ui/dom";

/*
 * Same open-state-driven transition as Sheet, one notch smaller: a popover is a
 * slip of paper, not a held sheet. See Sheet.tsx for why display/overlay are in
 * the property list and why `translate` is named directly.
 */
const popoverMotion = cn(
  "transition-[opacity,translate,overlay,display] transition-discrete duration-150 ease-paper",
  "opacity-0 translate-y-1",
  "open:opacity-100 open:translate-y-0",
  "starting:open:opacity-0 starting:open:translate-y-1",
  "motion-reduce:transition-none",
);

export interface PopoverProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "role" | "id" | "className" | "children" | "popover" | "onToggle"
> {
  open: boolean;
  /** Fires for platform-driven closes too: light dismiss, Escape, another auto popover opening. */
  onOpenChange: (open: boolean) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  placement?: Placement;
  /** Gap in px between anchor and paper. */
  offset?: number;
  /** Pin the width to the anchor's - what a select menu wants. */
  matchWidth?: boolean;
  role?: string;
  children: React.ReactNode;
  className?: string;
  id?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Non-modal floating paper anchored to a trigger. Popover API plus
 * @floating-ui/dom, so it lives in the top layer with light dismiss for free and
 * needs no portal. Children stay mounted while closed so ids stay resolvable.
 */
export function Popover({
  open,
  onOpenChange,
  anchorRef,
  placement = "bottom-start",
  offset = 6,
  matchWidth = false,
  role,
  children,
  className,
  id,
  ref,
  ...rest
}: PopoverProps) {
  const { popoverRef, popoverProps } = usePopover({ open, onOpenChange });
  useAnchorPosition(anchorRef, popoverRef, {
    placement,
    offset,
    matchWidth,
    enabled: open,
  });

  return (
    <div
      {...rest}
      id={id}
      role={role}
      ref={(node) => {
        popoverRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      className={cn(
        /* whitespace-normal and text-left: the popover renders in the top layer
           but still inherits text properties from wherever its anchor is
           mounted, so a trigger in a nowrap, right-aligned ledger cell would
           otherwise hand the menu unwrappable options and right-aligned text.
           See Sheet for the same declaration on the modal side. */
        "rounded-paper border border-rule bg-card p-1 font-body whitespace-normal text-left shadow-lift-lg",
        popoverMotion,
        className,
      )}
      {...popoverProps}
    >
      {children}
    </div>
  );
}
